import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/24/outline';
import styles from './ClientProject.module.css';
import ProjectInfo from './ClientProjectInfo';
import ProjectNote from './ClientProjectNote';
import {isProjectNoteAvailableAPI, projectListAPI} from '../../../services/client';

interface ClientProjectProps {
    projectId: number;
}

//클라이언트 프로젝트 상세 페이지 버튼
const ClientProject: React.FC<ClientProjectProps> = ({projectId}) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = React.useState<'info' | 'note'>('info');
    const [projectIds, setProjectIds] = useState<number[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isNoteAvailable, setIsNoteAvailable] = useState<boolean>(false);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                // 프로젝트 식별자 목록 API
                const response = await projectListAPI();
                setProjectIds(response);
                const index = response.findIndex(pid => pid === projectId);
                setCurrentIndex(index);
                const isAvailable = await isProjectNoteAvailableAPI(projectId);
                setIsNoteAvailable(isAvailable);
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        };

        fetchProjects();
    }, [projectId]);

    // Note 탭이 비활성화될 때 Info 탭으로 자동 전환
    useEffect(() => {
        if (!isNoteAvailable && activeTab === 'note') {
            setActiveTab('info');
        }
    }, [isNoteAvailable, activeTab]);

    //이전 프로젝트 페이지
    const handlePrevious = () => {
        if (currentIndex > 0) {
            const prevProjectId = projectIds[currentIndex - 1];
            navigate(`/client/projects/${prevProjectId}`);
        }
    };

    //다음 프로젝트 페이지
    const handleNext = () => {
        if (currentIndex < projectIds.length - 1) {
            const nextProjectId = projectIds[currentIndex + 1];
            navigate(`/client/projects/${nextProjectId}`);
        }
    };

    //프로젝트 식별자가 없는 경우
    if (!projectId) {
        return <div>Invalid project ID</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.navigationContainer}>
                {/*이전 프로젝트 버튼*/}
                <button
                    className={`${styles.navButton} ${currentIndex === 0 ? styles.disabled : ''}`}
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    aria-label="Previous project"
                >
                    <ChevronLeftIcon className={styles.navIcon}/>
                </button>

                <div className={styles.contentWrapper}>
                    <div className={styles.header}>
                        {/*탭 버튼, 프로젝트 정보/프로젝트 특이사항*/}
                        <div className={styles.tab}>
                            <button
                                className={`${styles.tabButton} ${activeTab === 'info' ? styles.active : ''}`}
                                onClick={() => setActiveTab('info')}
                            >
                                Info
                            </button>
                            {/* Note 탭은 isNoteAvailable이 true일 때만 표시 */}
                            {isNoteAvailable && (
                                <button
                                    className={`${styles.tabButton} ${activeTab === 'note' ? styles.active : ''}`}
                                    onClick={() => setActiveTab('note')}
                                >
                                    Note
                                </button>
                            )}
                        </div>
                    </div>

                    {activeTab === 'info' ? (
                        // 프로젝트 정보
                        <ProjectInfo projectId={projectId}/>
                    ) : (
                        // 프로젝트 특이사항 (isNoteAvailable이 true일 때만 렌더링)
                        isNoteAvailable && <ProjectNote projectId={projectId}/>
                    )}
                </div>

                {/*다음 프로젝트 버튼*/}
                <button
                    className={`${styles.navButton} ${currentIndex === projectIds.length - 1 ? styles.disabled : ''}`}
                    onClick={handleNext}
                    disabled={currentIndex === projectIds.length - 1}
                    aria-label="Next project"
                >
                    <ChevronRightIcon className={styles.navIcon}/>
                </button>
            </div>
        </div>
    );
};

export default ClientProject;